import React, { useRef } from "react";
import { SearchIcon } from "./Icons";

const SORT_OPTIONS_DOMAIN = [
	{ value: "relevance", label: "Relevance" },
	{ value: "compositeScore", label: "Composite Score" },
	{ value: "score", label: "Vector Score" },
	{ value: "domainReach", label: "Domain Reach" },
	{ value: "totalImpressions", label: "Impressions" },
	{ value: "avgBidCpm", label: "Avg Bid CPM" },
];

const SORT_OPTIONS_APP = [
	{ value: "relevance", label: "Relevance" },
	{ value: "compositeScore", label: "Composite Score" },
	{ value: "score", label: "Vector Score" },
	{ value: "domainReach", label: "App Reach" },
	{ value: "totalImpressions", label: "Impressions" },
];

export const CategorySearchInterface = ({
	subTab,
	onSubTabChange,

	searchInput,
	setSearchInput,
	currentThreshold,
	setCurrentThreshold,

	searchTerms = [],
	addSearchTerm,
	removeSearchTerm,
	clearSearchTerms,

	isSearching,
	onSearch,
	onClear,

	sortBy,
	setSortBy,
	sortDirection,
	setSortDirection,

	totalRecords = 0,
	hasSearched = false,
}) => {
	const inputRef = useRef(null);
	const thresholdRef = useRef(null);

	const sortOptions = subTab === "domains" ? SORT_OPTIONS_DOMAIN : SORT_OPTIONS_APP;
	const includeTerms = searchTerms.filter((t) => t.type === "include");
	const excludeTerms = searchTerms.filter((t) => t.type === "exclude");
	const canSearch = includeTerms.length > 0;

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (searchInput.trim()) addSearchTerm(searchInput, currentThreshold, "include");
		}
	};

	const handleSortOrderToggle = () => {
		setSortDirection((d) => (d === "DESC" ? "ASC" : "DESC"));
	};

	// The threshold box is intentionally uncontrolled. As a controlled input the
	// half-typed "0." keystroke parses to NaN → 0, React writes "0" back into the
	// box and the decimal point is swallowed — so every chip ended up with
	// threshold: 0 unless the step arrows were used.
	const handleThresholdChange = (e) => {
		const parsed = parseFloat(e.target.value);
		setCurrentThreshold(isNaN(parsed) ? 0 : Math.min(1, Math.max(0, parsed)));
	};

	// Snap the box to the clamped value once the user leaves it (e.g. typed 5 → 1)
	const handleThresholdBlur = () => {
		if (thresholdRef.current) {
			thresholdRef.current.value = String(currentThreshold);
		}
	};

	return (
		<div className="app-search-interface-container">
			{/* Search terms display row */}
			{searchTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span style={{ color: "#6b7280", marginRight: 6 }}>
								Category queries:
							</span>
							{includeTerms.map((term) => (
								<span key={term.id} className="search-term term-include">
									+{term.text}
									{term.threshold > 0 && (
										<span style={{ opacity: 0.7, fontSize: 10 }}>
											{" "}({term.threshold})
										</span>
									)}
									<button
										onClick={() => removeSearchTerm(term.id)}
										className="term-remove-btn"
									>
										×
									</button>
								</span>
							))}
							{excludeTerms.map((term) => (
								<span key={term.id} className="search-term term-exclude">
									−{term.text}
									{term.threshold > 0 && (
										<span style={{ opacity: 0.7, fontSize: 10 }}>
											{" "}({term.threshold})
										</span>
									)}
									<button
										onClick={() => removeSearchTerm(term.id)}
										className="term-remove-btn"
									>
										×
									</button>
								</span>
							))}
						</div>
					</div>
					<button className="btn-clear-all-inline" onClick={onClear}>
						Clear All
					</button>
				</div>
			)}

			{/* Search input row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<SearchIcon />
					<input
						ref={inputRef}
						type="text"
						placeholder="Enter a category concept, e.g. technology enthusiasts, sports fans..."
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyPress={handleKeyPress}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => addSearchTerm(searchInput, currentThreshold, "include")}
							className="term-type-btn include-btn"
							disabled={!searchInput.trim()}
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => addSearchTerm(searchInput, currentThreshold, "exclude")}
							className="term-type-btn exclude-btn"
							disabled={!searchInput.trim()}
						>
							Exclude
						</button>
					</div>
				</div>

				{/* Threshold input */}
				<div className="tier-input-container">
					<div className="performance-label-container">
						<label className="performance-filter-label">Threshold</label>
					</div>
					<input
						ref={thresholdRef}
						type="number"
						min="0"
						max="1"
						step="0.05"
						defaultValue={currentThreshold}
						onChange={handleThresholdChange}
						onBlur={handleThresholdBlur}
						className="performance-filter-select"
						style={{ width: 72 }}
						title="Similarity threshold (0 = unbounded, 0.6 = strict)"
					/>
				</div>

				<div className="search-button-container">
					<button
						type="button"
						onClick={onSearch}
						className="search-button"
						disabled={!canSearch || isSearching}
					>
						{isSearching ? (
							<>
								<div className="loading-spinner-small" />
								Searching...
							</>
						) : (
							"Search"
						)}
					</button>
				</div>
			</div>

			{/* Controls row: sub-tab + sort + filters toggle */}
			<div className="performance-filter-section">
				<div className="performance-filter-row">
					{/* Sub-tab (Domains / Apps) */}
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Search in</label>
						</div>
						<div className="sort-controls">
							<select
								value={subTab}
								onChange={(e) => onSubTabChange(e.target.value)}
								className="sort-select"
							>
								<option value="domains">Domains</option>
								<option value="apps">Apps</option>
							</select>
						</div>
					</div>

					{/* Sort by */}
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Sort by</label>
						</div>
						<div className="sort-controls">
							<select
								value={sortBy}
								onChange={(e) => setSortBy(e.target.value)}
								className="sort-select"
							>
								{sortOptions.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</select>
							<span
								onClick={handleSortOrderToggle}
								className="sort-order-arrow"
								title={sortDirection === "DESC" ? "Descending" : "Ascending"}
								style={{ cursor: "pointer", userSelect: "none" }}
							>
								{sortDirection === "DESC" ? "↓" : "↑"}
							</span>
						</div>
					</div>

					{/* Results count */}
					{hasSearched && (
						<div className="sort-container" style={{ justifyContent: "flex-end" }}>
							<div className="performance-label-container" />
							<span
								style={{
									fontSize: 12,
									color: "#6b7280",
									padding: "6px 0",
									whiteSpace: "nowrap",
								}}
							>
								{totalRecords.toLocaleString()} result{totalRecords !== 1 ? "s" : ""}
							</span>
						</div>
					)}
				</div>
			</div>

			<style jsx>{`
        .app-search-interface-container {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          box-sizing: border-box;
          margin-bottom: 16px;
        }

        .search-terms-display {
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          min-height: 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .search-terms-content {
          flex: 1;
          overflow: hidden;
        }

        .current-search-term {
          font-size: 13px;
          color: #374151;
          line-height: 1.5;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          align-items: center;
        }

        .search-term {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .term-include {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .term-exclude {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .term-remove-btn {
          margin-left: 3px;
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-weight: bold;
          padding: 0 2px;
          border-radius: 2px;
          line-height: 1;
          font-size: 14px;
        }

        .term-remove-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .btn-clear-all-inline {
          padding: 4px 12px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .btn-clear-all-inline:hover {
          background: #dc2626;
        }

        .nlp-mode-toggle {
          display: flex;
          gap: 4px;
          margin-bottom: 12px;
          background: #f1f5f9;
          border-radius: 6px;
          padding: 3px;
          width: fit-content;
        }

        .nlp-mode-btn {
          padding: 5px 14px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          background: transparent;
          color: #64748b;
          transition: all 0.15s;
        }

        .nlp-mode-btn.active {
          background: #fff;
          color: #1d4ed8;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .nlp-error-banner {
          margin-bottom: 10px;
          padding: 8px 12px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          font-size: 12px;
          color: #dc2626;
        }

        .nlp-search-button {
          background: #7c3aed;
        }

        .nlp-search-button:hover:not(:disabled) {
          background: #6d28d9;
        }

        .search-input-row {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 220px;
          display: flex;
          align-items: center;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          padding: 0 8px;
        }

        .search-input-wrapper :global(svg) {
          position: absolute;
          padding-left: 12px;
          width: 16px;
          height: 16px;
          color: #9ca3af;
          z-index: 1;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 12px 16px 12px 4px;
          font-size: 14px;
          background: transparent;
        }

        .search-input:focus {
          outline: none;
        }

        .search-input-wrapper:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .term-type-buttons {
          display: flex;
          gap: 4px;
          margin-right: 4px;
        }

        .term-type-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .include-btn {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .include-btn:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .exclude-btn {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        .exclude-btn:hover:not(:disabled) {
          background: #dc2626;
          border-color: #dc2626;
        }

        .term-type-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #d1d5db;
          cursor: not-allowed;
        }

        .tier-input-container {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 90px;
          flex-shrink: 0;
        }

        .search-button-container {
          display: flex;
          align-items: flex-end;
          flex-shrink: 0;
        }

        .search-button {
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          min-width: 100px;
        }

        .search-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .search-button:disabled {
          background: #d1d5db;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .performance-filter-section {
          margin-bottom: 4px;
        }

        .performance-filter-row {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .sort-container {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 120px;
        }

        .performance-label-container {
          display: flex;
          align-items: center;
          height: 20px;
        }

        .performance-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
          white-space: nowrap;
        }

        .performance-filter-select,
        .sort-select {
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 12px;
          background: white;
          color: #374151;
          outline: none;
          cursor: pointer;
        }

        .performance-filter-select:focus,
        .sort-select:focus {
          border-color: #0076bd;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
        }

        .sort-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .sort-order-arrow {
          font-size: 16px;
          color: #374151;
          font-weight: bold;
          padding: 4px 6px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
        }

        .sort-order-arrow:hover {
          background: #f3f4f6;
        }
      `}</style>
		</div>
	);
};
